import type { WordBoundary } from "@ai-teach/types";

export type TranscribedWord = { word: string; start: number; end: number };

type Timed = { start: number; end: number };

const LOOKAHEAD_WORDS = 5;
const MIN_WORD_SECONDS = 0.1;
const DEFAULT_WORD_SECONDS = 0.4;

function normalizeForMatch(value: string): string {
  return value.replace(/[^\w-]/g, "").toLowerCase();
}

/** The passage's words in render order — tokenized the way the app renders them. */
export function extractPassageWords(text: string): string[] {
  return text
    .split(/(\s+)/)
    .map((segment) => segment.replace(/[^\w'-]/g, ""))
    .filter((word) => word.length > 0);
}

function findUniqueIndex(
  normalizedWords: readonly string[],
  normalized: string,
  from: number
): number {
  let found = -1;
  for (let i = from; i < normalizedWords.length; i++) {
    if (normalizedWords[i] !== normalized) continue;
    if (found !== -1) return -1;
    found = i;
  }
  return found;
}

// Which passage word each transcribed word supplies a time for. Greedy and left-to-right within
// `LOOKAHEAD_WORDS`, then one resync scan accepting only a word that occurs exactly once in what
// remains — so speech that skips a clause recovers, while a repeated word stays ambiguous.
function matchTranscriptToWords(
  words: readonly string[],
  transcript: readonly TranscribedWord[]
): (Timed | null)[] {
  const normalizedWords = words.map(normalizeForMatch);
  const timings: (Timed | null)[] = words.map(() => null);
  let searchFrom = 0;

  for (const spoken of transcript) {
    const normalized = normalizeForMatch(spoken.word);
    if (!normalized) continue;

    let found = -1;
    const searchEnd = Math.min(searchFrom + LOOKAHEAD_WORDS, normalizedWords.length);
    for (let i = searchFrom; i < searchEnd; i++) {
      if (normalizedWords[i] === normalized && timings[i] === null) {
        found = i;
        break;
      }
    }
    if (found === -1) {
      found = findUniqueIndex(normalizedWords, normalized, searchFrom);
    }
    if (found === -1) continue;

    timings[found] = { start: spoken.start, end: spoken.end };
    searchFrom = found + 1;
  }

  return timings;
}

// Never widens past `to`: a run squeezed between two close anchors has to compress, or the words
// before the next anchor would get later offsets than the anchor itself. Offsets alone drive the
// highlight (a word holds until the next offset), so compressing is harmless where padding each
// word to a minimum spacing would reorder the passage.
function spreadEvenly(from: number, to: number, count: number): Timed[] {
  const span = Math.max(to - from, 0);
  const step = span / count;
  return Array.from({ length: count }, (_, i) => ({
    start: from + i * step,
    end: from + (i + 1) * step,
  }));
}

// Every passage word needs a time, including ones the transcript never matched (misheard, or
// spoken differently than written). Unmatched runs are spread across the silence their anchors
// leave, which keeps the order monotonic without inventing a transcript for them.
function fillUnmatchedWords(matched: readonly (Timed | null)[], durationSeconds: number): Timed[] {
  const total = matched.length;
  const fallbackEnd = durationSeconds > 0 ? durationSeconds : total * DEFAULT_WORD_SECONDS;
  const anchors: number[] = [];
  matched.forEach((timing, i) => {
    if (timing) anchors.push(i);
  });

  if (anchors.length === 0) return spreadEvenly(0, fallbackEnd, total);

  const result = spreadEvenly(0, fallbackEnd, total);
  for (const i of anchors) {
    const timing = matched[i];
    if (timing) result[i] = timing;
  }

  const firstAnchor = anchors[0] ?? 0;
  const lastAnchor = anchors[anchors.length - 1] ?? 0;
  const firstTiming = result[firstAnchor];
  const lastTiming = result[lastAnchor];

  if (firstAnchor > 0 && firstTiming) {
    const leading = spreadEvenly(0, firstTiming.start, firstAnchor);
    leading.forEach((timing, i) => {
      result[i] = timing;
    });
  }

  for (let a = 0; a < anchors.length - 1; a++) {
    const from = anchors[a] ?? 0;
    const to = anchors[a + 1] ?? 0;
    const gap = to - from - 1;
    const fromTiming = result[from];
    const toTiming = result[to];
    if (gap <= 0 || !fromTiming || !toTiming) continue;

    const inner = spreadEvenly(fromTiming.end, toTiming.start, gap);
    inner.forEach((timing, k) => {
      result[from + 1 + k] = timing;
    });
  }

  if (lastAnchor < total - 1 && lastTiming) {
    const count = total - 1 - lastAnchor;
    const end = Math.max(fallbackEnd, lastTiming.end + count * MIN_WORD_SECONDS);
    const trailing = spreadEvenly(lastTiming.end, end, count);
    trailing.forEach((timing, k) => {
      result[lastAnchor + 1 + k] = timing;
    });
  }

  return result;
}

/**
 * One timing per passage word, in render order, each labelled with the passage's own word. The
 * transcript supplies times only: a mis-transcription can shift a word's timing but can never
 * change its spelling, add a word, or drop one. Words the transcript never matched are estimated
 * from their neighbours, so the count always equals the passage's.
 */
export function buildPassageWordBoundaries(
  text: string,
  transcript: readonly TranscribedWord[],
  durationSeconds: number
): WordBoundary[] {
  const words = extractPassageWords(text);
  if (words.length === 0) return [];

  const filled = fillUnmatchedWords(matchTranscriptToWords(words, transcript), durationSeconds);

  return words.map((word, i) => {
    const timing = filled[i] ?? { start: 0, end: MIN_WORD_SECONDS };
    const seconds = Math.max(timing.end - timing.start, MIN_WORD_SECONDS);
    return { text: word, offset: timing.start * 1000, duration: seconds * 1000 };
  });
}

/** How many passage words the transcript actually located, for reporting estimate quality. */
export function countMatchedWords(
  text: string,
  transcript: readonly TranscribedWord[]
): { matched: number; total: number } {
  const words = extractPassageWords(text);
  const matched = matchTranscriptToWords(words, transcript).filter((t) => t !== null).length;
  return { matched, total: words.length };
}
